// Connection state, made visible.
//
// The failure the players actually hit was invisible: answers vanished and the
// screen just sat there. Two pieces fix the "am I broken or is it?" question:
//
//   ConnectivityBanner — a slim bar while offline, or when a write was given up
//                        on. A save itself is silent.
//   ConnectivityIcon   — an always-present signal dot, so a healthy connection
//                        is confirmable at a glance.
//
// Both are presentation-only; they read the shared snapshot from
// useConnectivity() (navigator.onLine + the durable write queue's backlog and
// what it has dropped). The banner's one write back is to say a loss was shown.

import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion as Motion } from 'framer-motion';
import { Signal, SignalLow, SignalZero } from 'lucide-react';
import { useConnectivity, acknowledgeLoss } from '../../hooks/useConnectivity';
import { DUR, EASE_BEZIER, liteMotion, prefersReducedMotion } from '../../lib/anim-tokens';

// A save is silent here too (Mel, 13 Oct 2026): while writes are in flight the
// dot turns amber and stays still, and its label does not change, so a screen
// reader hears nothing for a save. Only a lost connection is announced.
const ICON = {
  online: { Icon: Signal, color: 'text-wine-success', label: 'Connected' },
  syncing: { Icon: SignalLow, color: 'text-amber-500', label: 'Connected' },
  offline: { Icon: SignalZero, color: 'text-wine-error', label: 'Offline' },
};

export function ConnectivityIcon({ className = '' }) {
  const { status } = useConnectivity();
  const { Icon, color, label } = ICON[status] || ICON.online;
  // The offline pulse is decorative: the red glyph already says it, so weak
  // devices and reduced-motion players get the glyph without the pulse.
  const tone =
    status === 'offline' ? (liteMotion() ? '' : 'animate-pulse')
    : status === 'syncing' ? ''
    : 'opacity-40';

  return (
    <div
      className={`pointer-events-none fixed right-2 top-2 z-[60] ${className}`}
      role="status"
      aria-label={`Connection: ${label}`}
    >
      <Icon aria-hidden="true" className={`h-4 w-4 ${color} ${tone}`} />
    </div>
  );
}

// What the banner SHOWS is not the raw connection status. A save is silent: while
// writes are in flight and after they land there is no bar at all. The bar has two
// looks, and one persistent element carries both (it is never keyed by status, so
// it does not remount when the look changes):
//
//   offline — red, while the phone has no connection.
//   lost    — amber, "We couldn't save one of your answers." and its siblings (see
//             lossText), held for LOST_HOLD_MS. It is the only way a player learns
//             that a write was given up on.
export const LOST_HOLD_MS = 6000; // the "couldn't save" message is held this long
const EXIT_MS = DUR.med * 1000; // the bar's ease-out; it unmounts after this

const OPEN = { height: 'auto', opacity: 1 };
const CLOSED = { height: 0, opacity: 0 };

// The bar arriving, changing state and leaving is essential motion, so with
// reduced motion it still happens — by fading only, no height tween.
function barMotion(reduceMotion, leaving) {
  const target = leaving ? CLOSED : OPEN;
  return {
    initial: reduceMotion ? { opacity: 0 } : CLOSED,
    animate: reduceMotion ? { opacity: target.opacity } : target,
    transition: { duration: DUR.med, ease: EASE_BEZIER.softOut },
  };
}

const NO_LOSS = { answer: 0, rating: 0, other: 0 };

// The copy for a loss: plain, no apology, nothing to press. Only answers lost,
// or only a rating lost, say so; anything else or a mix is "part of".
function lossText({ answer, rating, other }) {
  if (answer > 0 && rating === 0 && other === 0) {
    return answer === 1
      ? "We couldn't save one of your answers."
      : `We couldn't save ${answer} of your answers.`;
  }
  if (rating > 0 && answer === 0 && other === 0) {
    return "We couldn't save your rating for this wine.";
  }
  return "We couldn't save part of your tasting.";
}

const hasLoss = (l) => l.answer + l.rating + l.other > 0;
const sameLoss = (a, b) =>
  a.answer === b.answer && a.rating === b.rating && a.other === b.other;

// kind: null (no bar) | 'offline' | 'lost'. `leaving` is the bar easing out while
// it still shows its last kind; it is not a kind of its own, so a loss that is
// found mid-exit simply flips back to 'lost' in the same element.
//
// An empty queue is not proof of a save: a write also leaves it when it is
// dropped (refused for good, expired, token wait timed out). `owed` is what the
// queue has dropped and the player has not been told about, by what was lost.
// The queue keeps that count, not this bar, so it does not matter whether the
// bar was mounted, or saw any writes waiting, when the write was dropped.
//
// Whenever something is owed, the queue is empty and the phone is online, the bar
// shows 'lost', a message that names what went missing, and holds it. While
// writes are still going it waits, so the message is told once, for the lot. A
// clean save shows nothing and starts no timers.
//
// A loss stops being owed when its message has been on screen for the full hold,
// and only then: the bar acknowledges what it showed as the hold completes. A
// hold cut short (the phone goes offline, the bar unmounts) acknowledges nothing,
// so the message is shown again in full. A loss that arrives during the hold
// joins the message (combined count, fresh hold) and is acknowledged with it.
// Writes going out during the hold neither shorten nor repeat it.
function useBannerState(status, pending, owed) {
  const [kind, setKindState] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [lostText, setLostText] = useState('');
  const kindRef = useRef(null);
  // The loss on screen while its hold runs: what the hold acknowledges when it
  // completes. NO_LOSS at any other time.
  const shownRef = useRef(NO_LOSS);
  const timersRef = useRef({ hold: null, leave: null });
  const { answer, rating, other } = owed;

  const setKind = useCallback((next) => {
    kindRef.current = next;
    setKindState(next);
  }, []);

  // Also ends a hold without acknowledging it: what it showed is still owed.
  const clearTimers = useCallback(() => {
    const t = timersRef.current;
    clearTimeout(t.hold);
    clearTimeout(t.leave);
    timersRef.current = { hold: null, leave: null };
    shownRef.current = NO_LOSS;
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const leave = useCallback(() => {
    if (timersRef.current.leave) return; // already easing out
    clearTimeout(timersRef.current.hold);
    setLeaving(true);
    timersRef.current.leave = setTimeout(() => {
      timersRef.current.leave = null;
      setKind(null);
      setLeaving(false);
    }, EXIT_MS);
  }, [setKind]);

  const showLost = useCallback(
    (loss) => {
      clearTimers(); // a message already showing or easing out restarts its hold
      shownRef.current = loss;
      setLostText(lossText(loss));
      setLeaving(false);
      setKind('lost');
      timersRef.current.hold = setTimeout(() => {
        timersRef.current.hold = null;
        const shown = shownRef.current;
        shownRef.current = NO_LOSS;
        // The exit starts first, so that if anything is still owed once this is
        // acknowledged, showing it is what cancels the exit and not the reverse.
        leave();
        acknowledgeLoss(shown); // it has been read for its full hold
      }, LOST_HOLD_MS);
    },
    [clearTimers, setKind, leave],
  );

  useEffect(() => {
    if (status === 'offline') {
      // The offline bar takes over. A message it cuts short stays owed.
      clearTimers();
      setLeaving(false);
      setKind('offline');
      return;
    }

    const wasOffline = kindRef.current === 'offline';
    const loss = { answer, rating, other };
    if (pending === 0 && hasLoss(loss)) {
      const holding = kindRef.current === 'lost' && timersRef.current.hold !== null;
      // Already on screen for exactly this loss: its hold runs on.
      if (holding && sameLoss(shownRef.current, loss)) return;
      showLost(loss);
      return;
    }

    // Nothing to tell now: nothing is owed, or writes are still going and what
    // is owed is told when the queue empties. A message already on screen is
    // left to run; only the offline bar has to be sent away.
    if (wasOffline) leave();
  }, [status, pending, answer, rating, other, clearTimers, setKind, leave, showLost]);

  return { kind, leaving, lostText };
}

export function ConnectivityBanner() {
  const { status, pending, owed = NO_LOSS } = useConnectivity();
  const { kind, leaving, lostText } = useBannerState(status, pending, owed);

  // The offline copy depends on whether writes are waiting. Hold the count seen
  // while the phone was offline, so the bar easing out after the connection
  // returns keeps its sentence instead of flipping as the queue drains.
  const [held, setHeld] = useState(pending);
  if (status === 'offline' && held !== pending) setHeld(pending);
  const shownPending = status === 'offline' ? pending : held;

  if (kind === null) return null;

  const offline = kind === 'offline';
  const reduceMotion = prefersReducedMotion();

  // Deliberately reassuring and specific. A player who has just tapped "Lock In"
  // needs to know the answer is held, not lost — that is the exact thing that
  // went wrong for them before.
  let message;
  if (offline) {
    message =
      shownPending > 0
        ? "You're offline — your answers are saved on this phone and will send when you reconnect."
        : "You're offline — hang tight, we'll reconnect you automatically.";
  } else {
    message = lostText;
  }

  // The lost message sits on amber with the dark ink: white on amber-500 is about
  // 2.1:1, ink on it about 6.5:1.
  const tone = offline ? 'bg-wine-error text-white' : 'bg-amber-500 text-tasted-ink';

  const textFade = { duration: reduceMotion ? 0 : DUR.fast, ease: EASE_BEZIER.softOut };

  return (
    <Motion.div
      {...barMotion(reduceMotion, leaving)}
      className="fixed inset-x-0 top-0 z-[59] overflow-hidden"
      role="status"
      aria-live="polite"
    >
      <div
        className={`px-4 py-2 text-center text-sm font-medium transition-colors duration-300 ${tone}`}
      >
        <div className="min-h-[1.25rem]">
          {/* The whole row (icon, text) is the cross-faded unit, so the outgoing
              row keeps its own icon until it has faded. */}
          <AnimatePresence mode="wait" initial={false}>
            <Motion.div
              // The message is in the key so a change of sentence cross-fades.
              key={`${kind}:${message}`}
              className="flex items-center justify-center gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={textFade}
            >
              {offline && <SignalZero aria-hidden="true" className="h-4 w-4 shrink-0" />}
              <span>{message}</span>
            </Motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Motion.div>
  );
}

export default ConnectivityBanner;
